"use client";
import React, { useState } from "react";
import { useRouter } from "next/navigation";

import { QRPreviewData } from "../QRPreview";
import { Button } from "@/components/Button";
import { Typography } from "@/components/Typography";
import { PreviewSelectorRow } from "./PreviewSelectorRow";

import { QR_PAGE_INDEX, updateIndex } from "@/utils/qrEditorNavigatorHelper";
import { generateQrDataClone, setNewQRData } from "@/utils/qrEditorHelper";

import { useToast } from "@/hooks/ui/useToast";

import { UserContext } from "@/contexts/UserContext";
import { QREditorContext } from "@/contexts/QREditorContext";
import { AmplitudeContext } from "@/contexts/AmplitudeContext/AmplitudeContext";

import { PreviewProps } from "./types";

import "./styles.scss";

const getQrTypeActive = (previewId: string) => (previewId === "links" ? "listoflinks" : previewId);

const getActivePreviewId = (initialPreview: string | undefined, activePreview: string) =>
  initialPreview ?? activePreview;

export const Preview = ({
  items,
  dataQA,
  ctaStyle,
  previewTranslations,
  defaultPreviewTitle,
}: PreviewProps) => {
  const router = useRouter();
  const { toast } = useToast();
  const qrEditorContext = React.useContext(QREditorContext);
  const userContext = React.useContext(UserContext);
  const { setSource } = React.useContext(AmplitudeContext);

  const [initialPreview, setInitialPreview] = React.useState(items[0].id);
  const [activePreview, setActivePreview] = useState(items[0].id);

  const [isQRSelected, setIsQRSelected] = React.useState(false);
  const [formURL, setFormURL] = React.useState("");

  const resolvedActiveId = getActivePreviewId(initialPreview, activePreview);
  const qrTypeActive = getQrTypeActive(resolvedActiveId);

  const activePreviewItem = items.find(
    (item) => item.id === initialPreview || item.id === activePreview
  );

  React.useEffect(() => {
    setIsQRSelected(false);
    qrEditorContext?.setEventSource(undefined);
    qrEditorContext?.setGridSelectEvent(false);
    setNewQRData(qrEditorContext, initialPreview, initialPreview);
  }, []);

  React.useEffect(() => {
    if (isQRSelected) {
      if (qrEditorContext?.qrData.currentPageIndex < QR_PAGE_INDEX.PAYMENT) {
        setSource("cta_buttons");
        qrEditorContext.setEventSource("cta_buttons");
        updateIndex({
          newIndex: qrEditorContext?.qrData.currentPageIndex + 1,
          qrEditorContext,
          userContext,
          toast,
          router,
        });

        router.push(formURL);
      }
    }
  }, [isQRSelected]);

  const handlePreviewButton = (url: string) => {
    const newQRData = generateQrDataClone(qrEditorContext, qrTypeActive);
    if (newQRData) newQRData.currentPageIndex = 0;
    setTimeout(() => {
      qrEditorContext?.setQrData(newQRData);
    }, 0);
    setFormURL(url);
    setIsQRSelected(true);
  };

  const updatePreview = (type: string) => {
    setActivePreview(type);
    setInitialPreview(undefined);
    const qrType = getQrTypeActive(type);
    if (!isQRSelected) {
      setNewQRData(qrEditorContext, qrType, qrType);
    }
  };

  const renderContentCard = () => {
    if (!activePreviewItem) {
      return null;
    }

    return (
      <div className="Preview__content">
        {activePreviewItem.richtext.map((richtextItem, i: number) => (
          <Typography
            key={i}
            Tag={richtextItem.tag}
            size={richtextItem.size}
            spacing={richtextItem.spacing}
          >
            {richtextItem.list
              ? richtextItem.list.map((e, ind: number) => (
                  <li key={ind}>{<span dangerouslySetInnerHTML={{ __html: e["item"] }}></span>}</li>
                ))
              : richtextItem.content}
          </Typography>
        ))}

        <Button
          onClick={() => {
            setSource("cta_buttons");
            handlePreviewButton(`${activePreviewItem.ctaLink}/${qrTypeActive}`);
          }}
          label={activePreviewItem.ctaLabel}
          dataQA={dataQA}
          rounded={!!ctaStyle}
          full
        />
      </div>
    );
  };

  const renderPhonePreview = () => (
    <div className="Preview__preview">
      <QRPreviewData
        data={qrEditorContext?.qrData}
        previewTranslations={previewTranslations}
        isQRInit={true}
        defaultPreviewTitle={defaultPreviewTitle}
      />
    </div>
  );

  return (
    <div
      data-testid="Preview"
      className="Preview"
    >
      <PreviewSelectorRow
        items={items}
        activeId={resolvedActiveId}
        onSelect={updatePreview}
      />

      <div className="Preview__body">
        {renderPhonePreview()}
        <div className="Preview__card">{renderContentCard()}</div>
      </div>
    </div>
  );
};
